import {
	SingleBulletinApplicantProfile,
} from '@/types/bulletins';
import { type ApiV1 } from '@teamworksdev/influencer/core';
import { EVENTS, trackEvent } from '@/plugins/gainsight';
import type { AxiosRequestConfig } from 'axios';
import {
	getList,
	getSingle,
	patch,
	post,
	remove,
} from './utilities/provider';

const RESOURCE = 'exchange/bulletins';

export function useBulletinsService(config?: Omit<AxiosRequestConfig, 'key'>) {
	const getBulletins = async (params?: ApiV1.GetBulletinsRequestParams) => {
		const response = await getList<ApiV1.GetBulletinsResponse>(RESOURCE, {
			key: 'getBulletins',
			params,
			...config,
		});
		return response.data;
	};

	const getBulletin = async (uuid: string) => {
		const response = await getSingle<ApiV1.GetSingleBulletinResponse>(RESOURCE, uuid, {
			key: 'getBulletin',
			...config,
		});
		return response.data.data;
	};

	const getBulletinAudienceReach = async (params?: ApiV1.GetBulletinReachParams) => {
		const response = await getSingle<ApiV1.BulletinReach>(`${RESOURCE}/audience-reach`, '', {
			key: 'getBulletinAudienceReach',
			params,
			...config,
		});
		return response.data;
	};

	const createBulletin = async (data: ApiV1.CreateBulletinRequestModel) => {
		const response = await post<ApiV1.GetSingleBulletinResponse>(RESOURCE, data, {
			key: 'createBulletin',
			...config,
		});

		const { type, name } = response.data.data;
		trackEvent(EVENTS.EXCHANGE_BULLETIN_CREATED, { type, name });

		return response.data.data;
	};

	const createBulletinApplication = async (uuid: string, data: ApiV1.CreateBulletinApplicationRequestModel) => {
		const response = await post<ApiV1.CreateBulletinApplicationResponse>(`${RESOURCE}/${uuid}/applications`, data, {
			key: 'createBulletinApplication',
			...config,
		});
		return response.data.data;
	};

	const updateBulletin = async (uuid: string, data: Partial<ApiV1.UpdateBulletinRequestModel>) => {
		const response = await patch(RESOURCE, uuid, data, {
			key: 'updateBulletin',
			...config,
		});
		return response.status;
	};

	const archiveBulletin = async (bulletinUuid: string) => {
		const response = await remove(RESOURCE, bulletinUuid, {
			key: 'archiveBulletin',
			...config,
		});
		return response.status;
	};

	const renewBulletin = async (bulletinUuid: string) => {
		const response = await patch(`${RESOURCE}/${bulletinUuid}/renew`, '', {
			key: 'renewBulletin',
			...config,
		});
		return response.status;
	};

	const getBulletinApplicants = async (uuid: string, params?: ApiV1.GetBulletinApplicantsRequestParams) => {
		const response = await getList<ApiV1.BulletinApplicant>(`${RESOURCE}/${uuid}/applications`, {
			key: 'getBulletinApplicants',
			params,
			...config,
		});
		return response.data;
	};

	const getBulletinApplicant = async (bulletinUuid: string, uuid: string) => {
		const response = await getSingle<ApiV1.GetSingleBulletinApplicantResponse>(`${RESOURCE}/${bulletinUuid}/applications`, uuid, {
			key: 'getBulletinApplicant',
			...config,
		});
		return new SingleBulletinApplicantProfile(response.data.data);
	};

	return {
		getBulletins,
		getBulletin,
		getBulletinAudienceReach,
		getBulletinApplicants,
		getBulletinApplicant,
		createBulletin,
		createBulletinApplication,
		updateBulletin,
		archiveBulletin,
		renewBulletin,
	};
}
